// apps/web/src/utils/mediaSanitizer.js
// SECURITY: Media content sanitization and validation

import DOMPurify from 'dompurify';
import { marked } from 'marked';

/**
 * SECURITY: Sanitize HTML content to prevent XSS attacks
 * - Strips all script tags
 * - Removes event handlers (onclick, onerror, etc.)
 * - Whitelists safe tags only
 * - Removes data: URIs except for images
 */
export function sanitizeHTML(html) {
  if (!html || typeof html !== 'string') return '';

  // Configure DOMPurify with strict settings
  const config = {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr', 'span', 'div',
      'ul', 'ol', 'li',
      'strong', 'em', 'u', 'i', 'b',
      'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'blockquote', 'code', 'pre'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'width', 'height',
      'class', 'id', 'style' // Limited style support
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'link'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
  };

  return DOMPurify.sanitize(html, config);
}

/**
 * SECURITY: Render markdown safely
 * - Parses markdown to HTML
 * - Sanitizes the resulting HTML
 */
export function sanitizeMarkdown(markdown) {
  if (!markdown || typeof markdown !== 'string') return '';

  // Configure marked for safe rendering
  marked.setOptions({
    breaks: true,
    gfm: true,
    headerIds: false,
    mangle: false
  });

  const html = marked.parse(markdown);
  return sanitizeHTML(html);
}

/**
 * SECURITY: Validate and sanitize video embed URLs
 * ONLY allows YouTube and Vimeo embeds
 * Returns null if URL is not from a trusted platform
 */
export function validateVideoEmbed(url) {
  if (!url || typeof url !== 'string') return null;

  try {
    const parsed = new URL(url);

    // YouTube embed validation
    if (parsed.hostname === 'www.youtube.com' || parsed.hostname === 'youtube.com') {
      const videoId = parsed.searchParams.get('v');
      if (videoId && /^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
        return {
          platform: 'youtube',
          embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`,
          thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
          videoId
        };
      }
    }

    // YouTube embed URL (already in embed format)
    if (parsed.hostname === 'www.youtube.com' && parsed.pathname.startsWith('/embed/')) {
      const videoId = parsed.pathname.split('/embed/')[1].split('?')[0];
      if (/^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
        return {
          platform: 'youtube',
          embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`,
          thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
          videoId
        };
      }
    }

    // YouTube no-cookie embed URL (already in our preferred format)
    if (parsed.hostname === 'www.youtube-nocookie.com' && parsed.pathname.startsWith('/embed/')) {
      const videoId = parsed.pathname.split('/embed/')[1].split('?')[0];
      if (/^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
        return {
          platform: 'youtube',
          embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`,
          thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
          videoId
        };
      }
    }

    // YouTube short URLs
    if (parsed.hostname === 'youtu.be') {
      const videoId = parsed.pathname.substring(1);
      if (/^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
        return {
          platform: 'youtube',
          embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`,
          thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
          videoId
        };
      }
    }

    // Vimeo validation
    if (parsed.hostname === 'vimeo.com' || parsed.hostname === 'www.vimeo.com') {
      const videoId = parsed.pathname.split('/').filter(p => p)[0];
      if (videoId && /^\d+$/.test(videoId)) {
        return {
          platform: 'vimeo',
          embedUrl: `https://player.vimeo.com/video/${videoId}`,
          thumbnail: null, // Vimeo requires API call for thumbnail
          videoId
        };
      }
    }

    // Vimeo embed URL
    if (parsed.hostname === 'player.vimeo.com' && parsed.pathname.startsWith('/video/')) {
      const videoId = parsed.pathname.split('/video/')[1];
      if (/^\d+$/.test(videoId)) {
        return {
          platform: 'vimeo',
          embedUrl: `https://player.vimeo.com/video/${videoId}`,
          thumbnail: null,
          videoId
        };
      }
    }

    // Not a trusted platform
    return null;

  } catch (err) {
    console.error('[mediaSanitizer] Invalid video URL:', err);
    return null;
  }
}

/**
 * SECURITY: Validate image URLs
 * - Only allows HTTPS URLs or relative paths starting with /uploads/
 * - Blocks data: URIs to prevent embedded malicious code
 */
export function validateImageUrl(url) {
  if (!url || typeof url !== 'string') return null;

  // Allow relative paths to uploaded files
  if (url.startsWith('/uploads/')) {
    return url;
  }

  // Allow HTTPS URLs only
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'https:') {
      return url;
    }
  } catch (err) {
    // Invalid URL
  }

  return null;
}

/**
 * Sanitize entire media panel object
 * Validates all content and attachments
 */
export function sanitizeMediaPanel(mediaPanel) {
  if (!mediaPanel || typeof mediaPanel !== 'object') return null;

  const sanitized = {
    enabled: Boolean(mediaPanel.enabled),
    content_type: ['markdown', 'html', 'richtext', 'plaintext'].includes(mediaPanel.content_type)
      ? mediaPanel.content_type
      : 'plaintext',
    content: '',
    attachments: [],
    settings: {
      panel_width: mediaPanel.settings?.panel_width || '400px',
      panel_height: mediaPanel.settings?.panel_height || 'auto',
      allow_resize: Boolean(mediaPanel.settings?.allow_resize ?? true),
      default_collapsed: Boolean(mediaPanel.settings?.default_collapsed ?? false)
    }
  };

  // Sanitize content based on type
  if (mediaPanel.content) {
    switch (sanitized.content_type) {
      case 'html':
      case 'richtext':
        sanitized.content = sanitizeHTML(mediaPanel.content);
        break;
      case 'markdown':
        // Store raw markdown, sanitize on render
        sanitized.content = String(mediaPanel.content);
        break;
      case 'plaintext':
      default:
        sanitized.content = String(mediaPanel.content);
    }
  }

  // Sanitize attachments
  if (Array.isArray(mediaPanel.attachments)) {
    sanitized.attachments = mediaPanel.attachments
      .map(attachment => {
        if (attachment.type === 'image') {
          const validUrl = validateImageUrl(attachment.url);
          if (validUrl) {
            return {
              id: attachment.id || crypto.randomUUID(),
              type: 'image',
              url: validUrl,
              caption: String(attachment.caption || ''),
              width: Number(attachment.width) || null,
              height: Number(attachment.height) || null
            };
          }
        } else if (attachment.type === 'video') {
          const validated = validateVideoEmbed(attachment.embed_url);
          if (validated) {
            return {
              id: attachment.id || crypto.randomUUID(),
              type: 'video',
              platform: validated.platform,
              embed_url: validated.embedUrl,
              thumbnail: validated.thumbnail,
              title: String(attachment.title || ''),
              videoId: validated.videoId
            };
          }
        }
        return null;
      })
      .filter(Boolean);
  }

  return sanitized;
}
